<template>
    <div class="deals">
        <div class="deals__top">
            <div class="deals__top-title">
                <h1>Сделки</h1>
            </div>
        </div>

        <div v-if="preloader"
             class="deals__tabs-preloader">
            <div />
        </div>
        <div v-else-if="tabs.length && deals.length"
             class="deals__tabs">
            <STTabs :list="tabs"
                    @click="changeTab" />
        </div>

        <STDivider />

        <div class="deals__content">
            <div v-if="preloader"
                 class="deals__content-preloader">
                <div />
                <div />
                <div />
                <div />
                <div />
                <div />
                <div />
            </div>

            <InfoBlock v-else-if="isError"
                       :type="2"
                       title="Ошибка синхронизации"
                       description="К&nbsp;сожалению, в&nbsp;данный момент мы&nbsp;не&nbsp;можем отобразить список ваших сделок.<br/>Мы&nbsp;уже работаем над решеним проблемы.<br/>Попробуйте войти в&nbsp;личный кабинет позже."
                       :modify="['info-block_theme_vertical']" />

            <div v-else-if="!deals?.length">
                <InfoBlock :type="1"
                           title="У Вас нет сделок."
                           description="После заключения сделки, информация о ней появится на данной странице."
                           :modify="['info-block_theme_vertical']" />
            </div>

            <div v-else
                 class="transition-block"
                 :style="{ opacity: animationOpacity, display: animationDisplay}">
                <DealsHeader :sort="sort"
                             @changeSort="changeSort" />
                <DealsItems :deals="deals" />
            </div>
        </div>

        <DealsSidebar ref="dealsSidebar"
                      :deal-info="selectedDeal" />
    </div>
</template>

<script>
import DealsItems from '@/blocks/DealsBlock/DealsItems/DealsItems';
import DealsHeader from '@/blocks/DealsBlock/DealsHeader/DealsHeader';
import DealsSidebar from '@/blocks/DealsBlock/DealsSidebar/DealsSidebar';
import InfoBlock from '@/blocks/InfoBlock/InfoBlock';
import {mapActions, mapGetters} from 'vuex';
import {DEALS_STATUSES} from '@/helpers/constants';

export default {
    name: 'Deals',

    components: {
        DealsItems,
        DealsHeader,
        DealsSidebar,
        InfoBlock
    },

    data() {
        return {
            tabs: [
                { id: 'deals', name: 'Сделки', count: 0 }
            ],                          // табы
            deals           : [],       // сделки
            animationOpacity: 1,        // анимация - время перехода
            animationDisplay: 'block',  // анимация - тип отображения,
            preloader       : true,     // происходит ли подгрузка данных
            isError         : false,    // есть ли ошибка в получении данных
            sort            : {
                projectName: {
                    active: false,
                    order : 'desc'
                },
                objectCost: {
                    active: false,
                    order : 'desc'
                },
                newStatusName: {
                    active: false,
                    order : 'desc'
                }
            },                          // сортировка
        };
    },

    computed: {
        ...mapGetters({
            selectedDeal: 'deals/GET_SELECTED_DEAL'
        }),
        ...mapGetters({
            projects: 'GET_PROJECTS'
        }),
    },

    watch: {
        /**
         * При выборе сделки, открывает сайдбар со сделкой
         * или закрывает если сделки нет
         * @param value
         */
        selectedDeal(value) {
            if (value) {
                this.$refs.dealsSidebar?.showSidebar();
            } else {
                this.$refs.dealsSidebar?.hideSidebar();
            }
        }
    },

    async created() {
        if (!this.projects?.length) {
            await this.$store.dispatch('getProjectsInfo');
        }

        await this.initDeals();
    },

    methods: {
        ...mapActions('deals', ['getDeals']),

        /**
         * Запрашивает сделки клиента
         * @return {Promise<void>}
         */
        async initDeals() {
            this.preloader = true;

            await this.getDeals()
                .then((response) => {
                    const {data} = response;

                    if (data.error) {
                        this.showError();

                        throw new Error(`В запросе произошла ошибка - ${data.error}`);
                    }

                    // messageCode
                    // 0 - Ошибка интеграции
                    // 1 - Данные переданы
                    // 2 - Истек срок жизни AccessToken
                    // 3 - Некорректный AccessToken
                    // 4 - userId не заполнен
                    const isShowError = !data.data || !data.data.messageCode || data.data.messageCode === 0;

                    if (isShowError) {
                        this.showError();
                        throw new Error('Ошибка интеграции');
                    }

                    switch (data.data.messageCode) {
                        case 1:
                            this.setDataDeals(data.data.leads);
                            break;
                        case 2:
                            console.error('Истек срок жизни AccessToken');
                            break;
                        case 3:
                            console.error('Некорректный AccessToken');
                            break;
                        case 4:
                            console.error('userId не заполнен');
                            break;
                    }
                })
                .catch((error) => {
                    this.showError();

                    throw new Error(error);
                })
                .finally(() => {
                    setTimeout(() => {
                        this.preloader = false;
                    }, 1500);
                });
        },

        /**
         * Формирует сделки
         * @param leads
         */
        setDataDeals(leads) {
            // DEALS_STATUSES
            this.deals = leads.reduce((result, lead) => {
                const project = this.projects.find((item) => item.id?.toLowerCase() === lead.projectId?.toLowerCase());

                const projectLink = project?.link || '#';
                const projectName = project?.name;

                const status = DEALS_STATUSES.find((item) => item.id?.toLowerCase() === lead.leadStatusId?.toLowerCase());

                if (status && !status.hide) {
                    result.push({
                        ...lead,
                        projectLink,
                        projectName,
                        newStatusName: status.text
                    });
                }

                return result;
            }, []);

            this.tabs[0].count = this.deals.length;
        },

        /**
         * Изменяет сортировку
         * @param key
         */
        changeSort(key) {
            for (const item in this.sort) {
                if (this.sort.hasOwnProperty(item)) {
                    this.sort[item].active = false;
                }
            }

            this.sort[key].active = true;
            this.sort[key].order = this.sort[key].order === 'desc' ? 'asc' : 'desc';

            this.sortDeals(key, this.sort[key].order);
        },

        /**
         * Сортирует сделки
         * @param key
         * @param order
         */
        sortDeals(key, order) {
            if (order === 'desc') {
                this.deals.sort((a, b) => a[key] > b[key] ? 1 : -1);
            } else {
                this.deals.sort((a, b) => b[key] > a[key] ? 1 : -1);
            }
        },

        /**
         * Показывает ошибку
         */
        showError() {
            this.isError = true;
        },

        changeTab(id) {
            //
        }
    }
};
</script>

<style lang="scss">
@import "Deals";
</style>
